import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoIcon from '@mui/icons-material/Info';
import TokenIcon from '@mui/icons-material/Token';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VerifiedIcon from '@mui/icons-material/Verified';
import WebhookIcon from '@mui/icons-material/Webhook';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
	Button,
	Card,
	Chip,
	CircularProgress,
	Container,
	Divider,
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography
} from "@mui/material";
import { green, red, yellow } from "@mui/material/colors";
import { styled } from '@mui/material/styles';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { format, parseISO } from "date-fns";
import React, { useCallback, useContext, useState } from "react";
import { makeStyles } from 'tss-react/mui';

import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircle from '@mui/icons-material/CheckCircle';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import Edit from '@mui/icons-material/Edit';
import CropFree from '@mui/icons-material/QrCode2';
import MessageIcon from '@mui/icons-material/Message';
import SignalCellular4Bar from '@mui/icons-material/SignalCellular4Bar';
import SignalCellularConnectedNoInternet0Bar from '@mui/icons-material/SignalCellularConnectedNoInternet0Bar';
import SignalCellularConnectedNoInternet2Bar from '@mui/icons-material/SignalCellularConnectedNoInternet2Bar';
import TableRowSkeleton from "../../components/TableRowSkeleton";

import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import CreateOrEditWhatsAppModal from "../../components/CreateOrEditWhatsAppModal";
import UniServiceIframeModal from "../../components/UniServiceIframeModal";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

import { Can } from "../../components/Can";
import { AuthContext } from "../../context/Auth/AuthContext";

import AvTimerIcon from '@mui/icons-material/AvTimer';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';

import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import useSettings from '../../hooks/useSettings';

import Avatar from '@mui/material/Avatar';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';

import FacebookIcon from '@mui/icons-material/Facebook';
import ForumIcon from '@mui/icons-material/Forum';
import InstagramIcon from '@mui/icons-material/Instagram';
import TelegramIcon from '@mui/icons-material/Telegram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ToastError from "../../toast/error/toastError";
import ToastSuccess from "../../toast/success/toastSuccess";

const useStyles = makeStyles()((theme) => {
	return {
		mainPaper: {
			flex: 1,
			padding: theme.spacing(1),
			overflowY: "scroll",
			...theme.scrollbarStyles,
		},
		customTableCell: {
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
		},
		tooltip: {
			backgroundColor: "#f5f5f9",
			color: "rgba(0, 0, 0, 0.87)",
			fontSize: theme.typography.pxToRem(14),
			border: "1px solid #dadde9",
			maxWidth: 450,
		},
		tooltipPopper: {
			textAlign: "center",
		},
		buttonProgress: {
			color: green[500],
		},
	}
});

const CustomToolTip = ({ title, content, children }) => {
	const { classes } = useStyles();

	return (
		<Tooltip
			arrow
			classes={{
				tooltip: classes.tooltip,
				popper: classes.tooltipPopper,
			}}
			title={
				<React.Fragment>
					<Typography gutterBottom color="inherit">
						{title}
					</Typography>
					{content && <Typography>{content}</Typography>}
				</React.Fragment>
			}
		>
			{children}
		</Tooltip>
	);
};

const Connections = () => {
	const { classes } = useStyles();
	const { themeStretch } = useSettings();
	const { user } = useContext(AuthContext);
	const { whatsApps, loading } = useContext(WhatsAppsContext);
	const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
	const [qrModalOpen, setQrModalOpen] = useState(false);
	const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	// Modal iframe pra Login com Facebook (Embedded Signup) via UniService.
	// Aberto SEM `selectedWhatsApp` desabilitaria o modal; aqui mantemos o
	// próprio selectedWhatsApp pra passar contract + businessAccountId.
	const [uniServiceIframeOpen, setUniServiceIframeOpen] = useState(false);
	const [selectedWebhookUrl, setSelectedWebhookUrl] = useState(null);
	const handleOpenUniService = async (whatsApp) => {
		const t0 = Date.now();
		// eslint-disable-next-line no-console
		console.log("[uniservice-prepare] clique 'Login com Facebook'", {
			whatsappId: whatsApp?.id,
			name: whatsApp?.name,
			contract: whatsApp?.uniserviceContract,
			businessAccountId: whatsApp?.uniserviceBusinessAccountId || null
		});
		setSelectedWhatsApp(whatsApp);
		let businessAccountId = whatsApp.uniserviceBusinessAccountId || null;
		let webhookUrl = null;
		try {
			// prepare agora é INSTANTÂNEO (não chama o UniService) — só devolve a
			// webhookUrl deste UniChat (domínio ou ip:porta) + o businessAccountId
			// atual. O iframe usa a webhookUrl pra criar a webhook subscription no
			// UniService direto do navegador. O timeout é só uma rede de segurança.
			const { data } = await api.post(
				`/whatsapp/${whatsApp.id}/uniservice-prepare`,
				null,
				{ timeout: 8000 }
			);
			if (data?.businessAccountId) businessAccountId = data.businessAccountId;
			if (data?.webhookUrl) webhookUrl = data.webhookUrl;
			// eslint-disable-next-line no-console
			console.log("[uniservice-prepare] OK", {
				ms: Date.now() - t0,
				businessAccountId,
				webhookUrl
			});
		} catch (err) {
			// eslint-disable-next-line no-console
			console.warn("[uniservice-prepare] sem resposta — abrindo iframe assim mesmo", {
				ms: Date.now() - t0,
				code: err?.code,
				status: err?.response?.status,
				message: err?.message
			});
		}
		setSelectedWebhookUrl(webhookUrl);
		setSelectedWhatsApp(prev => ({
			...(prev || whatsApp),
			uniserviceBusinessAccountId: businessAccountId
		}));
		setUniServiceIframeOpen(true);
	};
	const handleCloseUniService = () => setUniServiceIframeOpen(false);

	// Menu de ações para a conexão CONNECTED uniservice: gerenciar no FB,
	// desconectar (mantém remota viva), desconectar e remover do UniService.
	const [menuAnchor, setMenuAnchor] = useState(null);
	const [menuWhatsApp, setMenuWhatsApp] = useState(null);
	const handleOpenMenu = (event, whatsApp) => {
		setMenuAnchor(event.currentTarget);
		setMenuWhatsApp(whatsApp);
	};
	const handleCloseMenu = () => {
		setMenuAnchor(null);
		setMenuWhatsApp(null);
	};

	// Abre o WhatsApp Manager do Facebook na conta do cliente. O parâmetro
	// correto é `waba_id` (WhatsApp Business Account id) — `business_id`
	// refere-se ao Business Manager pai, que não temos aqui. Se faltar wabaId,
	// abre o WhatsApp Manager genérico e o usuário escolhe a conta.
	const handleManageOnFacebook = () => {
		if (!menuWhatsApp) return;
		const wabaId = menuWhatsApp.uniserviceWabaId;
		const url = wabaId
			? `https://business.facebook.com/wa/manage/phone-numbers/?waba_id=${encodeURIComponent(wabaId)}`
			: "https://business.facebook.com/wa/manage/home/";
		window.open(url, "_blank", "noopener,noreferrer");
		handleCloseMenu();
	};

	const handleDisconnect = async (deleteRemote) => {
		if (!menuWhatsApp?.id) return;
		const target = menuWhatsApp;
		handleCloseMenu();
		try {
			await api.post(`/whatsapp/${target.id}/uniservice-disconnect`, {
				deleteRemote: !!deleteRemote
			});
			ToastSuccess(
				deleteRemote
					? "Conexão removida do UniService."
					: "Conexão desvinculada desta plataforma."
			);
		} catch (err) {
			ToastError(err);
		}
	};

	// Acionado quando o iframe do UniService completa (FB.login concluído OU
	// usuário reaproveitou conexão existente da lista). Pula apenas quando
	// o id remoto não mudou E a row já tem subscription ativa — assim não
	// duplicamos subscription. Quando o id está salvo mas SEM subscription
	// (resíduo de versão antiga), seguimos pra que o backend crie a
	// subscription e grave o secret.
	const handleUniServiceSuccess = async ({
		businessAccountId,
		previousBusinessAccountId,
		subscriptionId,
		secret,
		signupState,
		isActive,
		wabaId,
		phoneNumber,
	}) => {
		if (!businessAccountId || !selectedWhatsApp?.id) return;
		const currentRemoteId = selectedWhatsApp.uniserviceBusinessAccountId;
		try {
			if (subscriptionId && secret) {
				// O iframe JÁ criou a webhook subscription no UniService (do
				// navegador). Aqui só gravamos o vínculo local — o backend NÃO
				// precisa alcançar o UniService, então funciona mesmo com a
				// conectividade backend→UniService indisponível. Mandamos também
				// o estado da conta (signupState/isActive/wabaId/phone) pra o
				// backend marcar o status na hora, sem esperar o sync remoto.
				await api.post(`/whatsapp/${selectedWhatsApp.id}/uniservice-link`, {
					businessAccountId,
					subscriptionId,
					apiKey: secret,
					signupState,
					isActive,
					wabaId,
					displayPhoneNumber: phoneNumber,
				});
			} else {
				// Fallback (iframe antigo / sem webhookUrl): o backend faz o
				// subscribe via reassign (precisa alcançar o UniService).
				const hasSubscription = !!selectedWhatsApp.uniserviceSubscriptionId;
				if (currentRemoteId === businessAccountId && hasSubscription) return;
				await api.put(`/whatsapp/${selectedWhatsApp.id}/uniservice-account`, {
					businessAccountId,
					previousBusinessAccountId: previousBusinessAccountId || currentRemoteId,
				});
			}
		} catch (err) {
			ToastError(err);
		}
	};
	const confirmationModalInitialState = {
		action: "",
		title: "",
		message: "",
		whatsAppId: "",
		open: false,
	};
	const [confirmModalInfo, setConfirmModalInfo] = useState(
		confirmationModalInitialState
	);

	const handleStartWhatsAppSession = async whatsAppId => {
		try {
			await api.post(`/whatsappsession/${whatsAppId}`);
		} catch (err) {
			ToastError(err);
		}
	};

	const handleRequestNewQrCode = async whatsAppId => {
		try {
			await api.put(`/whatsappsession/${whatsAppId}`);
		} catch (err) {
			ToastError(err);
		}
	};

	const handleOpenWhatsAppModal = () => {
		setSelectedWhatsApp(null);
		setWhatsAppModalOpen(true);
	};

	const handleCloseWhatsAppModal = useCallback(() => {
		setWhatsAppModalOpen(false);
		setSelectedWhatsApp(null);
	}, [setSelectedWhatsApp, setWhatsAppModalOpen]);

	const handleOpenQrModal = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setQrModalOpen(true);
	};

	const handleCloseQrModal = useCallback(() => {
		setSelectedWhatsApp(null);
		setQrModalOpen(false);
	}, [setQrModalOpen, setSelectedWhatsApp]);

	const handleEditWhatsApp = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setWhatsAppModalOpen(true);
	};

	// Renderiza o canal. Pra uniservice (API oficial Meta), retorna um chip
	// destacado com selo "Cloud API · Meta" + linha secundária com WABA/Contract.
	const renderChannel = (channel, whatsApp) => {
		const name = channel[0].toUpperCase() + channel.substring(1);

		if (channel === 'uniservice') {
			const waba = whatsApp?.uniserviceWabaId;
			const contract = whatsApp?.uniserviceContract;
			const verified = !!waba;
			return (
				<Stack alignItems="center" spacing={0.5}>
					<Chip
						size="small"
						icon={verified
							? <VerifiedIcon style={{ color: '#1877F2' }} />
							: <WhatsAppIcon style={{ color: '#25D366' }} />}
						label="WhatsApp Cloud API · Meta"
						sx={{
							fontWeight: 600,
							letterSpacing: 0.2,
							bgcolor: verified ? 'rgba(24,119,242,0.08)' : 'rgba(37,211,102,0.10)',
							color: verified ? '#1877F2' : '#0d8a3f',
							border: '1px solid',
							borderColor: verified ? 'rgba(24,119,242,0.35)' : 'rgba(37,211,102,0.35)',
						}}
					/>
					{(waba || contract) && (
						<Typography
							variant="caption"
							sx={{ color: 'text.secondary', fontSize: 11, lineHeight: 1.2 }}
						>
							{waba && <>WABA <strong>{String(waba).slice(0, 8)}…</strong></>}
							{waba && contract && <span style={{ margin: '0 6px' }}>·</span>}
							{contract && <>Contract <strong>{contract}</strong></>}
						</Typography>
					)}
				</Stack>
			);
		}

		switch (channel) {
			case 'whatsapp': return <div style={{ display: 'flex', justifyContent: 'center' }}><WhatsAppIcon style={{ color: '#0dc242' }} /><p style={{ marginLeft: 5 }}>{name}</p></div>;
			case 'telegram': return <div style={{ display: 'flex', justifyContent: 'center' }}><TelegramIcon style={{ color: '#3390ec' }} /><p style={{ marginLeft: 5 }}>{name}</p></div>;
			case 'instagram': return <div style={{ display: 'flex', justifyContent: 'center' }}><InstagramIcon style={{ color: '#fe0079' }} /><p style={{ marginLeft: 5 }}>{name}</p></div>;
			case 'messenger': return <div style={{ display: 'flex', justifyContent: 'center' }}><FacebookIcon style={{ color: '#0573e7' }} /><p style={{ marginLeft: 5 }}>{name}</p></div>;
			case 'webchat': return <div style={{ display: 'flex', justifyContent: 'center' }}><ForumIcon style={{ color: '#0573e7' }} /><p style={{ marginLeft: 5 }}>{name}</p></div>;
			default: return null;
		}
	}

	const renderMessagesStatus = whatsApp => {
		return (
			<div className={classes.customTableCell}>
				{whatsApp.ignoreChatbot ? (
					<CustomToolTip
						title={i18n.t("connections.table.messagesDisabled") || "Mensagens Desabilitadas"}
						content={i18n.t("connections.table.messagesDisabledDesc") || "Esta conexão não enviará mensagens automáticas"}
					>
						<DoNotDisturbIcon style={{ color: '#ff9800' }} />
					</CustomToolTip>
				) : (
					<CustomToolTip
						title={i18n.t("connections.table.messagesEnabled") || "Mensagens Ativas"}
						content={i18n.t("connections.table.messagesEnabledDesc") || "Esta conexão enviará mensagens automáticas"}
					>
						<MessageIcon style={{ color: green[500] }} />
					</CustomToolTip>
				)}
			</div>
		);
	};

	const handleOpenConfirmationModal = (action, whatsAppId) => {
		if (action === "disconnect") {
			setConfirmModalInfo({
				action: action,
				title: i18n.t("connections.confirmationModal.disconnectTitle"),
				message: i18n.t("connections.confirmationModal.disconnectMessage"),
				whatsAppId: whatsAppId,
			});
		}

		if (action === "delete") {
			setConfirmModalInfo({
				action: action,
				title: i18n.t("connections.confirmationModal.deleteTitle"),
				message: i18n.t("connections.confirmationModal.deleteMessage"),
				whatsAppId: whatsAppId,
			});
		}
		setConfirmModalOpen(true);
	};

	const handleSubmitConfirmationModal = async () => {
		if (confirmModalInfo.action === "disconnect") {
			try {
				await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
			} catch (err) {
				ToastError(err);
			}
		}

		if (confirmModalInfo.action === "delete") {
			try {
				await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
				ToastSuccess(i18n.t("connections.toasts.deleted"));
			} catch (err) {
				ToastError(err);
			}
		}

		setConfirmModalInfo(confirmationModalInitialState);
	};

	const renderActionButtons = whatsApp => {
		// API Oficial (UniService):
		//   - CONNECTED        → chip "Webhook OK" (ou alerta se subscription faltou) + menu
		//   - DISCONNECTED+WABA salvo → "Reconectar com Facebook" (variant outlined, ícone refresh)
		//   - sem WABA salvo   → "Login com Facebook" (variant contained, ícone FB)
		if (whatsApp.channel === "uniservice") {
			if (whatsApp.status === "CONNECTED") {
				const hasSubscription = !!whatsApp.uniserviceSubscriptionId;
				return (
					<Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
						<CustomToolTip
							title={hasSubscription ? "Webhook vinculado" : "Subscription pendente"}
							content={hasSubscription
								? "Eventos da Meta chegam via subscription ativa."
								: "Conta vinculada mas sem subscription de webhook. Reconecte para criar."}
						>
							<Chip
								size="small"
								icon={hasSubscription
									? <WebhookIcon style={{ fontSize: 14 }} />
									: <WarningAmberIcon style={{ fontSize: 14 }} />}
								label={hasSubscription ? "Webhook OK" : "Subscription?"}
								color={hasSubscription ? "success" : "warning"}
								variant="outlined"
								sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
							/>
						</CustomToolTip>
						<IconButton
							size="small"
							onClick={(e) => handleOpenMenu(e, whatsApp)}
							disabled={!whatsApp.uniserviceContract}
							aria-label="Gerenciar conexão"
						>
							<MoreVertIcon />
						</IconButton>
					</Stack>
				);
			}
			// DISCONNECTED com WABA salvo → reconectar (visual distinto). Sem WABA → login inicial.
			const hasSavedAccount = !!whatsApp.uniserviceBusinessAccountId;
			if (whatsApp.status === "DISCONNECTED" && hasSavedAccount) {
				return (
					<Button
						size="small"
						variant="outlined"
						startIcon={<RefreshIcon />}
						onClick={() => handleOpenUniService(whatsApp)}
						sx={{
							textTransform: "none",
							borderColor: '#1877F2',
							color: '#1877F2',
							'&:hover': {
								borderColor: '#1877F2',
								backgroundColor: 'rgba(24,119,242,0.06)',
							},
						}}
						disabled={!whatsApp.uniserviceContract}
					>
						Reconectar com Facebook
					</Button>
				);
			}
			return (
				<Button
					size="small"
					variant="contained"
					startIcon={<FacebookIcon />}
					onClick={() => handleOpenUniService(whatsApp)}
					sx={{
						textTransform: "none",
						bgcolor: '#1877F2',
						'&:hover': { bgcolor: '#0d65d9' },
					}}
					disabled={!whatsApp.uniserviceContract}
				>
					Login com Facebook
				</Button>
			);
		}

		return (
			<>
				{whatsApp.status === "qrcode" && (
					<Button
						size="small"
						variant="contained"
						color="primary"
						onClick={() => handleOpenQrModal(whatsApp)}
					>
						{i18n.t("connections.buttons.qrcode")}
					</Button>
				)}
				{whatsApp.status === "DISCONNECTED" && (
					<>
						<Button
							size="small"
							variant="contained"
							color="secondary"
							onClick={() => handleRequestNewQrCode(whatsApp.id)}
						>
							{i18n.t("connections.buttons.newQr")}
						</Button>
					</>
				)}
				{(whatsApp.status === "CONNECTED" ||
					whatsApp.status === "PAIRING" ||
					whatsApp.status === "TIMEOUT") && (
						<Button
							size="small"
							variant="outlined"
							color="secondary"
							onClick={() => {
								handleOpenConfirmationModal("disconnect", whatsApp.id);
							}}
						>
							{i18n.t("connections.buttons.disconnect")}
						</Button>
					)}
				{whatsApp.status === "OPENING" && (
					<Button
						size="small"
						variant="contained"
						color="primary"
					>
						Gerando &nbsp; <CircularProgress size={24} color="inherit" />
					</Button>
				)}
			</>
		);
	};

	// Status agora é chip semântico com label legível. Mantém tooltip pra detalhes
	// (i18n) e adiciona spinner inline pra OPENING.
	const renderStatusChip = whatsApp => {
		const map = {
			CONNECTED: {
				label: 'Conectado',
				color: 'success',
				variant: 'filled',
				icon: <SignalCellular4Bar style={{ fontSize: 16 }} />,
				title: i18n.t("connections.toolTips.connected.title"),
				content: null,
			},
			DISCONNECTED: {
				label: 'Desconectado',
				color: 'default',
				variant: 'outlined',
				icon: <SignalCellularConnectedNoInternet0Bar style={{ fontSize: 16 }} />,
				title: i18n.t("connections.toolTips.disconnected.title"),
				content: i18n.t("connections.toolTips.disconnected.content"),
			},
			OPENING: {
				label: 'Conectando…',
				color: 'warning',
				variant: 'outlined',
				icon: <CircularProgress size={12} thickness={6} color="inherit" />,
				title: 'Estabelecendo conexão',
				content: null,
			},
			qrcode: {
				label: 'Aguardando QR',
				color: 'info',
				variant: 'outlined',
				icon: <CropFree style={{ fontSize: 16 }} />,
				title: i18n.t("connections.toolTips.qrcode.title"),
				content: i18n.t("connections.toolTips.qrcode.content"),
			},
			TIMEOUT: {
				label: 'Sem sinal',
				color: 'warning',
				variant: 'outlined',
				icon: <SignalCellularConnectedNoInternet2Bar style={{ fontSize: 16 }} />,
				title: i18n.t("connections.toolTips.timeout.title"),
				content: i18n.t("connections.toolTips.timeout.content"),
			},
			PAIRING: {
				label: 'Pareando',
				color: 'warning',
				variant: 'outlined',
				icon: <SignalCellularConnectedNoInternet2Bar style={{ fontSize: 16 }} />,
				title: i18n.t("connections.toolTips.timeout.title"),
				content: i18n.t("connections.toolTips.timeout.content"),
			},
		};
		const cfg = map[whatsApp.status];
		if (!cfg) {
			return (
				<div className={classes.customTableCell}>
					<Chip size="small" label={whatsApp.status || '—'} variant="outlined" />
				</div>
			);
		}
		const chip = (
			<Chip
				size="small"
				icon={cfg.icon}
				label={cfg.label}
				color={cfg.color}
				variant={cfg.variant}
				sx={{ fontWeight: 600, '& .MuiChip-icon': { ml: '6px' } }}
			/>
		);
		return (
			<div className={classes.customTableCell}>
				<CustomToolTip title={cfg.title} content={cfg.content}>
					{chip}
				</CustomToolTip>
			</div>
		);
	};

	const HtmlTooltip = styled(({ className, ...props }) => (
		<Tooltip {...props} classes={{ popper: className }} />
	))(({ theme }) => ({
		[`& .${tooltipClasses.tooltip}`]: {
			backgroundColor: '#f5f5f9',
			color: 'rgba(0, 0, 0, 0.87)',
			maxWidth: 400,
			fontSize: theme.typography.pxToRem(12),
			border: '1px solid #dadde9',
		},
	}));

	function msToTime(milliseconds) {
		//Get hours from milliseconds
		var hours = milliseconds / (1000 * 60 * 60);
		var absoluteHours = Math.floor(hours);
		var h = absoluteHours > 9 ? absoluteHours : '0' + absoluteHours;

		//Get remainder from hours and convert to minutes
		var minutes = (hours - absoluteHours) * 60;
		var absoluteMinutes = Math.floor(minutes);
		var m = absoluteMinutes > 9 ? absoluteMinutes : '0' + absoluteMinutes;

		//Get remainder from minutes and convert to seconds
		var seconds = (minutes - absoluteMinutes) * 60;
		var absoluteSeconds = Math.floor(seconds);
		var s = absoluteSeconds > 9 ? absoluteSeconds : '0' + absoluteSeconds;

		return h + ':' + m + ':' + s;
	}

	const copyToken = (divId) => {
		let token = document.getElementById(`${divId}`);

		if (!token) {
			console.error("Element not found");
			return;
		}

		// Cria um campo de entrada temporário
		const tempInput = document.createElement('input');
		tempInput.value = token.value;
		document.body.appendChild(tempInput);

		// Seleciona o texto no campo de entrada temporário
		tempInput.select();
		tempInput.setSelectionRange(0, 99999); // Para dispositivos móveis

		// Executa o comando de cópia
		document.execCommand('copy');

		// Remove o campo de entrada temporário
		document.body.removeChild(tempInput);

		ToastSuccess(`${i18n.t("connections.toasts.copied")}`);
	};

	return (
		<>
			<div style={{ marginTop: '65px' }}>
				<ConfirmationModal
					title={confirmModalInfo.title}
					open={confirmModalOpen}
					onClose={setConfirmModalOpen}
					onConfirm={handleSubmitConfirmationModal}
				>
					{confirmModalInfo.message}
				</ConfirmationModal>
				<QrcodeModal
					open={qrModalOpen}
					onClose={handleCloseQrModal}
					whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id} />
				<CreateOrEditWhatsAppModal
					open={whatsAppModalOpen}
					onClose={handleCloseWhatsAppModal}
					whatsAppId={!qrModalOpen && selectedWhatsApp?.id} />
				<UniServiceIframeModal
					open={uniServiceIframeOpen}
					onClose={handleCloseUniService}
					onSuccess={handleUniServiceSuccess}
					contract={selectedWhatsApp?.uniserviceContract}
					businessAccountId={selectedWhatsApp?.uniserviceBusinessAccountId}
					connectionName={selectedWhatsApp?.name}
					webhookUrl={selectedWebhookUrl} />
				<Menu
					anchorEl={menuAnchor}
					open={Boolean(menuAnchor)}
					onClose={handleCloseMenu}
					anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
					transformOrigin={{ vertical: "top", horizontal: "right" }}
				>
					<MenuItem
						onClick={handleManageOnFacebook}
						disabled={!menuWhatsApp?.uniserviceWabaId}
					>
						<ListItemIcon>
							<OpenInNewIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText
							primary="Gerenciar no Facebook"
							secondary={menuWhatsApp?.uniserviceWabaId ? null : "WABA não vinculado"}
						/>
					</MenuItem>
					<Divider />
					<MenuItem onClick={() => handleDisconnect(false)}>
						<ListItemIcon>
							<LinkOffIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText
							primary="Desconectar desta plataforma"
							secondary="Mantém a conexão ativa no UniService"
						/>
					</MenuItem>
					<MenuItem onClick={() => handleDisconnect(true)} sx={{ color: "error.main" }}>
						<ListItemIcon>
							<DeleteForeverIcon fontSize="small" color="error" />
						</ListItemIcon>
						<ListItemText
							primary="Desconectar e remover do UniService"
							secondary="Apaga a conexão de todos os sistemas (irreversível)"
						/>
					</MenuItem>
				</Menu>
				<Page title={i18n.t("connections.title")}>
					<Container maxWidth={false}>
						<HeaderBreadcrumbs
							heading={i18n.t("connections.title")}
							action={<>
								<Can
									role={user.profile}
									perform="connections-page:addConnection"
									yes={() => (
										<Button
											variant="contained"
											color="primary"
											onClick={handleOpenWhatsAppModal}
										>
											{i18n.t("connections.buttons.add")}
										</Button>
									)} />
							</>} />
						<Card>
							<Scrollbar>
								<TableContainer sx={{ minWidth: 800 }} style={{ marginTop: '40px' }}>
									<Table >
										<TableHead>
											<TableRow>
												<TableCell align="center">
													{i18n.t("connections.table.channel")}
												</TableCell>
												<TableCell align="center">
													{i18n.t("connections.table.name")}
												</TableCell>
												<TableCell align="center">
													{i18n.t("connections.table.status")}
												</TableCell>
												<TableCell align="center">
													{i18n.t("connections.table.session")}
												</TableCell>
												<TableCell align="center">
													{i18n.t("connections.table.messages") || "Mensagens"}
												</TableCell>
												<TableCell align="center">
													{i18n.t("connections.table.autoClose")}
												</TableCell>
												<TableCell align="center">API Token</TableCell>
												<Can
													role={user.profile}
													perform="connections-page:editOrDeleteConnection"
													yes={() => (
														<TableCell align="center">
															{i18n.t("connections.table.actions")}
														</TableCell>
													)} />
											</TableRow>
										</TableHead>
										<TableBody>
											{loading ? (
												<TableRowSkeleton />
											) : (
												<>
													{whatsApps?.length > 0 &&
														whatsApps.map(whatsApp => {
															if (user.companyId != whatsApp.companyId) return null;
															const isUniservice = whatsApp.channel === 'uniservice';
															// Border-left colorido só pra uniservice (destaque "linha oficial Meta").
															// Verde quando conectado, laranja quando conectando, cinza quando off.
															const borderColor = !isUniservice
																? 'transparent'
																: whatsApp.status === 'CONNECTED'
																	? '#1877F2'
																	: whatsApp.status === 'OPENING'
																		? '#ed6c02'
																		: 'rgba(0,0,0,0.18)';
															return (
																<TableRow
																	key={whatsApp.id}
																	sx={{
																		borderLeft: `4px solid ${borderColor}`,
																		bgcolor: isUniservice ? 'rgba(24,119,242,0.025)' : 'transparent',
																		transition: 'border-color .15s ease, background-color .15s ease',
																	}}
																>
																	<TableCell align="center">{renderChannel(whatsApp.channel, whatsApp)}</TableCell>
																	<TableCell align="center">{whatsApp.name}</TableCell>
																	<TableCell align="center">
																		{renderStatusChip(whatsApp)}
																	</TableCell>
																	<TableCell align="center">
																		{renderActionButtons(whatsApp)}
																	</TableCell>
																	<TableCell align="center">
																		{renderMessagesStatus(whatsApp)}
																	</TableCell>
																	<TableCell align="center">
																		<div className={classes.customTableCell}>
																			{whatsApp.autoClose ? <>
																				<CheckCircle style={{ color: green[500] }} />
																				<HtmlTooltip
																					title={
																						<React.Fragment>
																							<List sx={{ width: '100%' }}>
																								<ListItem alignItems="flex-start">
																									<ListItemAvatar>
																										<Avatar>
																											<AvTimerIcon />
																										</Avatar>
																									</ListItemAvatar>
																									<ListItemText primary={i18n.t("connections.table.autoCloseTimeOut")}
																										secondary={
																											<React.Fragment>
																												{msToTime(whatsApp.autoCloseTime)}
																											</React.Fragment>
																										} />
																								</ListItem>
																								<ListItem>
																									<ListItemAvatar>
																										<Avatar>
																											<ForwardToInboxIcon />
																										</Avatar>
																									</ListItemAvatar>
																									<ListItemText primary={i18n.t("connections.table.autoCloseMessage")}
																										secondary={
																											<React.Fragment>
																												{whatsApp.autoCloseMessage}
																											</React.Fragment>
																										} />
																								</ListItem>
																							</List>
																						</React.Fragment>
																					}
																				>
																					<InfoIcon style={{ color: yellow[500] }} />
																				</HtmlTooltip>
																			</> : <>
																				<CancelIcon style={{ color: red[500] }} />
																			</>}
																		</div>
																	</TableCell>
																	<TableCell align="center">
																		<HtmlTooltip
																			title={
																				<React.Fragment>
																					<List sx={{ width: '100%' }}>
																						<ListItem alignItems="flex-start">
																							<ListItemText primary={'Token:'}
																								secondary={
																									<React.Fragment>
																										<div style={{ display: 'flex' }}>
																											<p style={{ maxWidth: 200 }}>{whatsApp.apiToken}</p>
																											<input type="text" hidden value={whatsApp.apiToken} id={`token_api_${whatsApp.id}`}></input>
																											<Button onClick={e => copyToken(`token_api_${whatsApp.id}`)}><ContentCopyIcon /></Button>
																										</div>
																									</React.Fragment>
																								} />
																						</ListItem>
																					</List>
																				</React.Fragment>
																			}
																		>
																			<TokenIcon />
																		</HtmlTooltip>
																	</TableCell>
																	<Can
																		role={user.profile}
																		perform="connections-page:editOrDeleteConnection"
																		yes={() => (
																			<TableCell align="center">
																				<IconButton
																					size="small"
																					onClick={() => handleEditWhatsApp(whatsApp)}
																				>
																					<Edit />
																				</IconButton>

																				<IconButton
																					size="small"
																					onClick={e => {
																						handleOpenConfirmationModal("delete", whatsApp.id);
																					}}
																				>
																					<DeleteOutline />
																				</IconButton>
																			</TableCell>
																		)} />
																</TableRow>
															);
														})}
												</>
											)}
										</TableBody>
									</Table>
								</TableContainer>
							</Scrollbar>
						</Card>
					</Container>
				</Page >
			</div>
		</>
	);
};

export default Connections;